import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Component, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useSession } from "../auth/session";
import { Dialog, Field, InlineAlert, MoreMenu, PageHeader, PageShell, StatusChip, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import type { CanvasHandle, Selected } from "./ProcessCanvas";
import {
  DRIVER_LABELS, FRAMES, INVENTORY_POOL_LABELS, RECORDED_IN, STAGE_LABELS, STAGES, dateLabel, dayLabel, parseHours, people, timeLabel, usd,
  type DraftView, type Pool, type ProcessView, type Step, type VersionRow,
} from "./model";
import "./process.css";

/**
 * The Process editor (ALN-32, docs/pages/process.md; reference screens 05 and 30-state-process-*).
 * The approved map, or this winery's draft for people who edit it at 1024px or wider. Every change
 * autosaves to the draft about 2 seconds after the last one; Submit for approval, Send back and
 * Approve move it to a new version. Money shows only with "See costs and inventory".
 */
const ProcessCanvas = lazy(() => import("./ProcessCanvas"));

const SAMPLE_SUBTITLE = "A sample production process, drawn as a process map (BPMN), for your winery to replace with its own. Change the flow with the tools on the left of the canvas, then select an activity to link it to a labor pool and a cost pool. Your changes save as a draft; approve the draft to make it the version programs use. Rates come from ";
const SAMPLE_SUBTITLE_END = ". Each activity also shows the Vintner module that records it, built or planned, so the map and the menu follow the same flow.";
const SUBTITLE = "Your winery's production process as a map (BPMN), from vineyard to warehouse, with the costs attached to each activity. Programs are planned from its approved version.";
const AUTOSAVE_MS = 2000;
const SETTLE_MS = 400;

type Save = { kind: "idle" } | { kind: "saving" } | { kind: "saved"; at: string } | { kind: "offline" } | { kind: "error"; message: string };

const WIDE = "(min-width: 1024px)";
function useWide() {
  return useSyncExternalStore(
    cb => { const m = window.matchMedia(WIDE); m.addEventListener("change", cb); return () => m.removeEventListener("change", cb); },
    () => window.matchMedia(WIDE).matches,
  );
}

class LoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <p className="proc__msg" role="alert">The process editor did not load. Check the connection and open this page again.</p> : this.props.children;
  }
}

export function ProcessPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const me = session.data?.user?.id ?? null;
  const wide = useWide();
  const search = useSearch();
  const [, navigate] = useLocation();
  const activityParam = new URLSearchParams(search).get("activity");

  const [nonce, setNonce] = useState(0);
  const [oldVersion, setOldVersion] = useState<number | null>(null);
  const cur = useQuery({ queryKey: ["process"], queryFn: () => api<ProcessView>("/process") });
  const data = cur.data;
  const showDraft = !!data?.canEdit && !!data.draft && oldVersion === null;
  const draftQ = useQuery({ queryKey: ["process", "draft", nonce], queryFn: () => api<DraftView>("/process/draft"), enabled: showDraft, staleTime: Infinity, gcTime: 0 });
  const oldQ = useQuery({ queryKey: ["process", "version", oldVersion], queryFn: () => api<ProcessView>(`/process/versions/${oldVersion}`), enabled: oldVersion !== null });

  const editable = !!data?.canEdit && wide && oldVersion === null;
  // An old version, or losing the permission, is read-only at once. The width alone switches the
  // canvas only once it settles and a waiting edit has saved (see the 1024px effect).
  const editableAtAnyWidth = !!data?.canEdit && oldVersion === null;
  const [canvasWide, setCanvasWide] = useState<boolean | null>(null);
  const shownEditable = editableAtAnyWidth && (canvasWide ?? wide);
  const shown = oldVersion !== null ? oldQ.data : data;
  const loading = cur.isPending || (showDraft && draftQ.isPending) || (oldVersion !== null && oldQ.isPending);
  /**
   * The map loaded into the canvas, taken once per load (a reload, or opening another version). The
   * first autosave creates the draft on the server; the canvas already shows it, so it isn't reloaded.
   */
  const loadKey = `${nonce}:${oldVersion ?? "cur"}`;
  const loadedRef = useRef<{ at: string; value: { key: string; xml: string; steps: Step[]; revision: number | null; baseVersion: number } } | null>(null);
  if (!loading && shown && loadedRef.current?.at !== loadKey) {
    loadedRef.current = {
      at: loadKey,
      value: showDraft && draftQ.data
        ? { key: `draft:${loadKey}`, xml: draftQ.data.bpmnXml, steps: draftQ.data.steps, revision: draftQ.data.revision, baseVersion: draftQ.data.baseVersion }
        : { key: `v${shown.version}:${loadKey}`, xml: shown.bpmnXml, steps: shown.steps.map(({ stage: _s, driver: _d, capitalize: _c, ...s }) => s), revision: null, baseVersion: shown.version },
    };
  }
  const loaded = loadedRef.current?.at === loadKey ? loadedRef.current.value : null;

  const canvas = useRef<CanvasHandle>(null);
  const steps = useRef(new Map<string, Step>());
  const revision = useRef<number | null>(null);
  const [, setTick] = useState(0);
  const [loads, setLoads] = useState(0); // bumped each time a canvas finishes loading its map
  const [ready, setReady] = useState(false);
  const [modelError, setModelError] = useState(false);
  const [sel, setSel] = useState<Selected | null>(null);
  const [frame, setFrame] = useState<string>(FRAMES[0].label);
  const [save, setSave] = useState<Save>({ kind: "idle" });
  const [approveError, setApproveError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | "submit" | "approve" | "sendBack" | "discard" | "versions" | "leave">(null);
  const leaveTo = useRef<string | null>(null);

  useEffect(() => {
    if (!loaded) return;
    steps.current = new Map(loaded.steps.map(s => [s.bpmnElementId, { ...s }]));
    revision.current = loaded.revision;
    setReady(false);
    setModelError(false);
    setSel(null);
    setSave({ kind: "idle" });
  }, [loaded]);

  // after the canvas loads: frame the first part of the map, or the activity a link names
  useEffect(() => {
    if (!ready || !canvas.current) return;
    // after layout settles, so the frame fits the canvas's final size
    const id = requestAnimationFrame(() => {
      if (!canvas.current) return;
      if (activityParam && canvas.current.select(activityParam)) { setFrame(""); return; } // centred on the activity, not on a lane group
      canvas.current.frame(FRAMES[0].stages);
      setFrame(FRAMES[0].label);
    });
    return () => cancelAnimationFrame(id);
  }, [ready, loads, activityParam]);

  /* ------------------------------------------------------------ autosave */
  const dirty = useRef(false);
  const saving = useRef<Promise<void> | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const baseVersion = loaded?.baseVersion ?? data?.version ?? 1;

  const doSave = useCallback(async (): Promise<void> => {
    window.clearTimeout(timer.current);
    if (saving.current) { await saving.current; if (!dirty.current) return; }
    if (!canvas.current || !dirty.current) return;
    dirty.current = false;
    setSave({ kind: "saving" });
    let failed = false;
    const run = (async () => {
      try {
        const bpmnXml = await canvas.current!.xml();
        const tasks = canvas.current!.tasks();
        const names = new Map(tasks.map(t => [t.id, t.name]));
        const body = [...steps.current.values()].filter(s => names.has(s.bpmnElementId)).map(s => ({ ...s, name: names.get(s.bpmnElementId) ?? s.name }));
        const res = await api<{ revision: number; savedAt: string; returnedToDraft: boolean }>("/process/draft", { method: "PUT", body: { revision: revision.current, baseVersion, bpmnXml, steps: body } });
        const started = revision.current === null;
        revision.current = res.revision;
        setSave(dirty.current ? { kind: "saving" } : { kind: "saved", at: res.savedAt });
        if (res.returnedToDraft) toast("Changed after submission. Submit it again for approval.");
        if (started || res.returnedToDraft) void qc.invalidateQueries({ queryKey: ["process"], exact: true });
      } catch (e) {
        failed = true;
        dirty.current = true;
        if (e instanceof ApiError && e.status !== 0) {
          setSave(e.status === 413 || e.status === 400 || e.status === 403 || e.status === 409 ? { kind: "error", message: e.message } : { kind: "offline" });
        } else setSave({ kind: "offline" });
      }
    })();
    saving.current = run;
    await run;
    saving.current = null;
    // changes made while this save was in flight save next; a failed save waits for the connection or the next change
    if (dirty.current && !failed) timer.current = window.setTimeout(() => void doSave(), AUTOSAVE_MS);
  }, [baseVersion, qc, toast]);

  const markDirty = useCallback(() => {
    if (!shownEditable) return;
    dirty.current = true;
    setSave({ kind: "saving" });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void doSave(), AUTOSAVE_MS);
  }, [doSave, shownEditable]);
  const flush = async () => { window.clearTimeout(timer.current); if (dirty.current || saving.current) await doSave(); };

  // retry when the connection is back
  useEffect(() => {
    const on = () => { if (dirty.current) void doSave(); };
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [doSave]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // leave guard: closing the tab, or following a link in the app, while changes haven't saved
  const unsaved = save.kind === "saving" || save.kind === "offline" || save.kind === "error";
  useEffect(() => {
    if (!unsaved) return;
    const before = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    const click = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.target === "_blank" || e.defaultPrevented || a.origin !== window.location.origin || a.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      leaveTo.current = a.pathname + a.search;
      setDialog("leave");
    };
    window.addEventListener("beforeunload", before);
    document.addEventListener("click", click, true);
    return () => { window.removeEventListener("beforeunload", before); document.removeEventListener("click", click, true); };
  }, [unsaved]);

  /* ------------------------------------------------------------ actions */
  /** Fetch the version and draft again, then load the canvas from them. */
  const reloadAll = async () => { await qc.invalidateQueries({ queryKey: ["process"], exact: true }); setNonce(n => n + 1); };
  // the window crossed 1024px: save any waiting edit first, then load the saved map again in the editor
  // or the viewer. If the save fails the canvas stays as it is, with the edit, until a save succeeds.
  // The width has to stay across 1024px for SETTLE_MS before the canvas switches: a dip that comes
  // straight back (a window being dragged, a full-page screenshot) doesn't reload the map.
  // A failed save isn't retried here: it waits for the connection or the next edit, as autosave does.
  const switching = useRef(false);
  const wideNow = useRef(wide);
  wideNow.current = wide;
  const [switchEnded, setSwitchEnded] = useState(0);
  const saveFailed = save.kind === "offline" || save.kind === "error";
  useEffect(() => {
    if (!data) return;
    if (canvasWide === null) { setCanvasWide(wide); return; }
    if (canvasWide === wide || switching.current) return;
    // the canvas looks the same either way for someone who can't edit it: nothing to save or reload
    if (!editableAtAnyWidth) { setCanvasWide(wide); return; }
    if (saveFailed) return;
    if (dirty.current && !saving.current) void flush(); // the waiting edit saves now; only the switch waits
    const id = window.setTimeout(() => {
      if (wideNow.current === canvasWide) return;
      switching.current = true;
      void (async () => {
        try { await flush(); } finally { switching.current = false; }
        // a failed save keeps the canvas as it is; the width may also have come back while it saved
        if (dirty.current) return;
        setSwitchEnded(n => n + 1);
        if (wideNow.current === canvasWide) return;
        setCanvasWide(wideNow.current);
        await reloadAll();
      })();
    }, SETTLE_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wide, data, canvasWide, editableAtAnyWidth, saveFailed, switchEnded]);
  const updateStep = (id: string, patch: Partial<Step>) => {
    const s = steps.current.get(id) ?? { bpmnElementId: id, name: sel?.name ?? "", overheadPoolId: null, laborPoolId: null, standardHours: null, inventoryPool: null };
    steps.current.set(id, { ...s, ...patch });
    setTick(t => t + 1);
    markDirty();
  };
  const copyXml = async () => {
    try {
      const xml = canvas.current ? await canvas.current.xml() : shown?.bpmnXml ?? "";
      await navigator.clipboard.writeText(xml);
      toast("BPMN XML copied.");
    } catch { toast("Copy failed. Select the text manually.", "error"); }
  };

  const draft = data?.draft ?? null;
  const submitted = !!draft?.submittedBy;
  const moreItems = [
    { label: "Versions", onSelect: () => setDialog("versions") },
    { label: "Copy BPMN XML", onSelect: () => void copyXml() },
    ...(editable && draft ? [{ label: "Discard draft", onSelect: () => setDialog("discard") }] : []),
  ];

  /* ------------------------------------------------------------ status */
  let statusText = "";
  if (data) {
    if (save.kind === "saving") statusText = "Saving draft…";
    else if (save.kind === "saved") statusText = `Draft saved ${timeLabel(save.at)}`;
    else if (save.kind === "offline") statusText = "Draft not saved. Check the connection. Your changes stay on this screen and save when it's back.";
    else if (save.kind === "error") statusText = "Draft not saved.";
    else if (draft && data.canEdit) statusText = `Draft saved ${timeLabel(draft.updatedAt)}`;
    else if (data.isSample) statusText = `Version ${data.version} (sample)`;
    else statusText = `Version ${data.version}, approved ${data.publishedAt ? dayLabel(data.publishedAt) : ""} by ${data.publishedBy ?? "Avero setup"}`;
  }

  const primary = editable && draft ? (
    submitted
      ? (data!.canApprove ? <>
          <button type="button" className="btn" onClick={() => setDialog("sendBack")}>Send back</button>
          <button type="button" className="btn btn--primary" onClick={() => { setApproveError(null); setDialog("approve"); }}>Approve</button>
        </> : null)
      : <button type="button" className="btn btn--primary" onClick={() => setDialog("submit")}>Submit for approval</button>
  ) : null;

  const subtitle = data?.isSample
    ? <>{SAMPLE_SUBTITLE}<Link href="/rates">Rates and pools</Link>{SAMPLE_SUBTITLE_END}</>
    : SUBTITLE;

  return (
    <PageShell>
      <PageHeader
        title="Process editor"
        subtitle={subtitle}
        actions={<>
          <span className="small muted proc__status" role="status">{statusText}</span>
          <MoreMenu name="Process" items={moreItems} />
          {primary}
        </>}
      />
      {cur.error && <InlineAlert variant="error">{(cur.error as Error).message}</InlineAlert>}
      {approveError && <InlineAlert variant="error">{approveError}</InlineAlert>}
      {save.kind === "error" && (
        <InlineAlert variant="error">
          <p className="proc__p">{save.message}</p>
          <button type="button" className="linkbtn" onClick={() => void reloadAll()}>Reload the draft</button>
        </InlineAlert>
      )}
      {data?.canEdit && !wide && oldVersion === null && <InlineAlert variant="info">Editing the process needs a screen at least 1024 pixels wide. You can read it here.</InlineAlert>}
      {oldVersion !== null && oldQ.data && (
        <InlineAlert variant="warning">
          <p className="proc__p">You're viewing version {oldVersion}, read-only. {oldQ.data.programsOnThisVersion} {oldQ.data.programsOnThisVersion === 1 ? "program still uses" : "programs still use"} it. <button type="button" className="linkbtn" onClick={() => setOldVersion(null)}>Back to the current version</button></p>
          <p className="proc__p">To bring something back, copy it into the draft.</p>
        </InlineAlert>
      )}
      {oldVersion === null && data?.canEdit && draft && <DraftCallout data={data} me={me} />}

      <div className="proc">
        <div className="proc__canvas">
          <div className="zoombar" role="group" aria-label="Zoom">
            <button type="button" className="btn btn--sm" aria-label="Zoom out" disabled={!ready} onClick={() => canvas.current?.zoom(-1)}>−</button>
            {FRAMES.map(f => (
              <button key={f.label} type="button" className="btn btn--sm" aria-pressed={frame === f.label} disabled={!ready}
                onClick={() => { canvas.current?.frame(f.stages.length ? f.stages : "other"); setFrame(f.label); }}>{f.label}</button>
            ))}
            <button type="button" className="btn btn--sm" aria-label="Zoom in" disabled={!ready} onClick={() => canvas.current?.zoom(1)}>+</button>
          </div>
          <div className="proc__frame">
            {!loaded || cur.error ? (
              <p className="proc__msg">{cur.error ? "This version of the process couldn't be opened. Open an earlier version from Versions, or ask Avero support." : "Loading the process editor…"}</p>
            ) : modelError ? (
              <p className="proc__msg" role="alert">This version of the process couldn't be opened. Open an earlier version from Versions, or ask Avero support.</p>
            ) : (
              <LoadBoundary>
                <Suspense fallback={<p className="proc__msg">Loading the process editor…</p>}>
                  <ProcessCanvas key={`${loaded.key}:${shownEditable}`} ref={canvas} xml={loaded.xml} editable={shownEditable}
                    onReady={() => { setReady(true); setLoads(n => n + 1); }} onError={() => setModelError(true)} onChange={markDirty} onSelect={setSel} />
                </Suspense>
              </LoadBoundary>
            )}
          </div>
        </div>
        {shown && (
          <ActivityPanel view={shown} sel={sel} step={sel ? steps.current.get(sel.id) ?? null : null} editable={shownEditable}
            onStep={updateStep} onName={(id, name) => canvas.current?.setName(id, name)} onLaneStage={(id, s) => canvas.current?.setLaneStage(id, s)} />
        )}
      </div>

      <SubmitDialog open={dialog === "submit"} onClose={() => setDialog(null)} onSubmit={async note => {
        await flush();
        const res = await api<{ revision: number }>("/process/draft/submit", { method: "POST", body: { revision: revision.current, note } });
        revision.current = res.revision;
        setDialog(null);
        toast("Sent for approval.");
        await qc.invalidateQueries({ queryKey: ["process"], exact: true });
      }} />
      {data && draft && (
        <ApproveDialog open={dialog === "approve"} version={Math.max(data.version, draft.baseVersion) + 1} note={draft.submitNote} onClose={() => setDialog(null)} onApprove={async requestId => {
          try {
            // an edit still waiting to save goes first; saving it sends the draft back to draft, so a stale map is never approved
            await flush();
            if (dirty.current) throw new Error("Your change hasn't saved yet, so nothing was approved. Check the connection and approve again.");
            const res = await api<{ version: number }>("/process/draft/approve", { method: "POST", body: { revision: revision.current ?? draft.revision }, headers: { "X-Request-Id": requestId } });
            setDialog(null);
            toast(`Version ${res.version} approved. New plans use it.`);
            void reloadAll();
          } catch (e) {
            setDialog(null);
            setApproveError(e instanceof Error ? e.message : "Approval failed. Try again.");
          }
        }} />
      )}
      <SendBackDialog open={dialog === "sendBack"} onClose={() => setDialog(null)} onSend={async note => {
        const res = await api<{ revision: number }>("/process/draft/send-back", { method: "POST", body: { note } });
        revision.current = res.revision;
        setDialog(null);
        toast("Sent back.");
        await qc.invalidateQueries({ queryKey: ["process"], exact: true });
      }} />
      <Dialog title="Discard the draft?" open={dialog === "discard"} onClose={() => setDialog(null)} actions={<>
        <button type="button" className="btn" onClick={() => setDialog(null)}>Cancel</button>
        <button type="button" className="btn btn--destructive" onClick={async () => {
          window.clearTimeout(timer.current);
          dirty.current = false;
          try {
            await api(`/process/draft?revision=${revision.current ?? draft?.revision ?? 0}`, { method: "DELETE" });
            setDialog(null);
            toast("Draft discarded.");
            void reloadAll();
          } catch (e) { setDialog(null); setSave({ kind: "error", message: (e as Error).message }); }
        }}>Discard</button>
      </>}>
        <p>Its changes since version {draft?.baseVersion ?? data?.version} are lost. Programs aren't affected.</p>
      </Dialog>
      <VersionsDialog open={dialog === "versions"} onClose={() => setDialog(null)} onOpen={async v => {
        setDialog(null);
        // a waiting edit saves before the canvas shows another version; if it can't, stay on the draft
        await flush();
        if (dirty.current) { toast("Your change hasn't saved yet. Check the connection, then open the version again."); return; }
        setOldVersion(v === data?.version ? null : v);
      }} />
      <Dialog title="Your last changes haven't saved yet. Leave and lose them?" open={dialog === "leave"} onClose={() => setDialog(null)} actions={<>
        <button type="button" className="btn" data-autofocus onClick={() => setDialog(null)}>Stay</button>
        <button type="button" className="btn btn--destructive" onClick={() => { const to = leaveTo.current; dirty.current = false; setSave({ kind: "idle" }); setDialog(null); if (to) navigate(to); }}>Leave</button>
      </>} />
    </PageShell>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function DraftCallout({ data, me }: { data: ProcessView; me: string | null }) {
  const d = data.draft!;
  if (d.submittedBy) {
    const mine = d.submittedById === me && !data.canApprove;
    return (
      <InlineAlert variant="info">
        <p className="proc__p">Sent for approval by {d.submittedBy} on {d.submittedAt ? dayLabel(d.submittedAt) : ""}{d.submitNote ? `: ${d.submitNote}` : ""}.</p>
        {mine && <p className="proc__p">Waiting for approval. Someone else has to approve it.</p>}
      </InlineAlert>
    );
  }
  return (
    <InlineAlert variant="info">
      <p className="proc__p">Draft based on version {d.baseVersion}, changed by {people(d.preparedBy)}. Programs use version {data.version} until the draft is approved.</p>
      {d.sentBack && <p className="proc__p">{d.sentBack.by} sent this back on {d.sentBack.at ? dayLabel(d.sentBack.at) : ""}: {d.sentBack.note}.</p>}
    </InlineAlert>
  );
}

function ActivityPanel({ view, sel, step, editable, onStep, onName, onLaneStage }: {
  view: ProcessView; sel: Selected | null; step: Step | null; editable: boolean;
  onStep(id: string, patch: Partial<Step>): void; onName(id: string, name: string): void; onLaneStage(id: string, stage: string | null): void;
}) {
  const money = !view.costsHidden;
  if (sel?.kind === "lane") {
    return (
      <section className="card proc__panel" aria-label="Activity costs">
        <div className="card__h"><div><p className="proc__eyebrow">Lane</p><h2 className="card__t">{sel.name || "Untitled lane"}</h2></div></div>
        <div className="card__c proc__fields">
          {editable ? (
            <Field label="Stage" tip={<Tip term="Stage" />} hint={sel.stage ? "Every activity in this lane takes this stage." : "Pick the stage for this lane. Activities in it can't be approved until it has one."}>
              <select className="select" value={sel.stage ?? ""} onChange={e => onLaneStage(sel.id, e.target.value || null)}>
                <option value="">Pick a stage</option>
                {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
            </Field>
          ) : (
            <ReadRow label="Stage" tip="Stage" value={sel.stage ? STAGE_LABELS[sel.stage] ?? sel.stage : "No stage yet"} hint="Every activity in this lane takes this stage." />
          )}
        </div>
      </section>
    );
  }
  if (sel?.kind !== "task") {
    return (
      <section className="card proc__panel" aria-label="Activity costs">
        <div className="card__h"><div><span className="field__lt"><h2 className="card__t">Activity costs</h2><Tip term="Activity costs" /></span><p className="card__d">Select an activity in the map.</p></div></div>
        <div className="card__c"><p className="proc__p muted small">Activities link to a labor pool and a cost pool, and name the inventory pool their cost lands in. Decisions and events don't carry cost.</p></div>
      </section>
    );
  }
  return <TaskPanel key={sel.id} view={view} sel={sel} step={step} editable={editable} money={money} onStep={onStep} onName={onName} />;
}

function TaskPanel({ view, sel, step, editable, money, onStep, onName }: {
  view: ProcessView; sel: Selected; step: Step | null; editable: boolean; money: boolean;
  onStep(id: string, patch: Partial<Step>): void; onName(id: string, name: string): void;
}) {
  const [name, setName] = useState(sel.name);
  const [hours, setHours] = useState(step?.standardHours == null ? "" : String(step.standardHours));
  const [hoursError, setHoursError] = useState<string | null>(null);
  useEffect(() => setName(sel.name), [sel.name]);

  const byId = new Map(view.pools.map(p => [p.id, p]));
  const cost = step?.overheadPoolId ? byId.get(step.overheadPoolId) ?? null : null;
  const labor = step?.laborPoolId ? byId.get(step.laborPoolId) ?? null : null;
  const costHidden = !!step?.overheadPoolId && !cost;
  const laborHidden = !!step?.laborPoolId && !labor;
  const stageText = sel.stage ? `${STAGE_LABELS[sel.stage] ?? sel.stage} (from its lane)` : sel.lane ? `None yet: pick the stage for the lane '${sel.lane.name}'` : "Not in a lane yet";
  const hoursNum = step?.standardHours ?? 0;
  const laborAtStandard = labor?.rateCents != null ? Math.round(hoursNum * labor.rateCents) : null;
  const companies = (kind: "labor" | "shared") => {
    const list = view.pools.filter(p => p.isActive && (kind === "labor" ? p.kind === "labor" : p.kind !== "labor"));
    const groups = new Map<string, Pool[]>();
    for (const p of list) groups.set(p.companyName, [...(groups.get(p.companyName) ?? []), p]);
    return [...groups.entries()];
  };
  const laborOption = (p: Pool) => (money && p.rateCents != null ? `${p.name} (${usd(p.rateCents)}/h)` : p.name);
  const costHint = costHidden ? "Linked to a pool in a company you don't have access to, so its name and rates are hidden."
    : cost ? `${cost.companyName} pool. Spread by ${DRIVER_LABELS[cost.driver] ?? cost.driver}${money && cost.rateCents != null ? ` at ${usd(cost.rateCents)} (current period)` : ""}. ${cost.capitalize ? "Capitalized." : "Expensed."}`
    : "No shared cost at this activity.";
  const laborHint = laborHidden ? "Linked to a pool in a company you don't have access to, so its name and rates are hidden."
    : labor ? `${labor.name}, ${labor.companyName}${money && labor.rateCents != null ? ` (${usd(labor.rateCents)}/h)` : ""}` : "No labor pool at this activity.";
  const noEstimate = !!step?.laborPoolId && !view.estimated.includes(sel.id);
  const estimateLine = noEstimate && (
    <span className="proc__est">No team estimate yet. {view.canStartEstimate ? <><Link href={`/estimate?activity=${encodeURIComponent(sel.id)}`}>Start one in Team estimates</Link>.</> : "Someone who sends surveys can start one in Team estimates."}</span>
  );
  const module = RECORDED_IN[sel.id];

  const commitName = () => { if (name.trim() !== sel.name) onName(sel.id, name.trim()); };
  const commitHours = () => {
    const h = parseHours(hours);
    if (!h.ok) { setHoursError("Enter a number of zero or more."); return; }
    setHoursError(null);
    if ((step?.standardHours ?? null) !== h.value) onStep(sel.id, { standardHours: h.value });
  };

  return (
    <section className="card proc__panel" aria-label="Activity costs">
      <div className="card__h"><div>
        <p className="proc__eyebrow">Activity</p>
        <h2 className="card__t">{sel.name || "Untitled activity"}</h2>
        <p className="card__d">Used by {view.programsOnThisVersion} {view.programsOnThisVersion === 1 ? "program" : "programs"}</p>
      </div></div>
      <div className="card__c proc__fields">
        {editable ? (
          <Field label="Name">
            <input className="input" value={name} maxLength={120} onChange={e => setName(e.target.value)} onBlur={commitName} onKeyDown={e => { if (e.key === "Enter") commitName(); }} />
          </Field>
        ) : <ReadRow label="Name" value={sel.name || "Untitled activity"} />}
        <div className="proc__derived">
          <span className="field__lt"><span>Stage</span><Tip term="Stage" /></span>
          <b>{stageText}</b>
        </div>
        {!sel.production ? <p className="proc__p muted small">This activity is outside the production pool, so it carries no cost.</p> : <>
          {editable && !costHidden ? (
            <Field label="Cost pool" hint={costHint}>
              <select className="select" value={step?.overheadPoolId ?? ""} onChange={e => onStep(sel.id, { overheadPoolId: e.target.value || null })}>
                <option value="">None</option>
                {companies("shared").map(([co, list]) => <optgroup key={co} label={co}>{list.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>)}
              </select>
            </Field>
          ) : <ReadRow label="Cost pool" value={costHidden ? "A pool in another company" : cost?.name ?? "None"} hint={costHint} />}
          {editable && !laborHidden ? (
            <Field label="Labor pool" tip={<Tip term="Labor pool" />} hint={laborHint}>
              <select className="select" value={step?.laborPoolId ?? ""} onChange={e => onStep(sel.id, { laborPoolId: e.target.value || null })}>
                <option value="">None</option>
                {companies("labor").map(([co, list]) => <optgroup key={co} label={co}>{list.map(p => <option key={p.id} value={p.id}>{laborOption(p)}</option>)}</optgroup>)}
              </select>
            </Field>
          ) : <ReadRow label="Labor pool" tip="Labor pool" value={laborHidden ? "A pool in another company" : labor?.name ?? "None"} hint={laborHint} />}
          {editable ? (
            <Field label="Standard hours per program" tip={<Tip term="Standard hours per program" />} error={hoursError}
              hint={<>Start from a team estimate; actuals replace it over time.{estimateLine && <> {estimateLine}</>}</>}>
              <input className="input num" type="text" inputMode="decimal" value={hours} onChange={e => setHours(e.target.value)} onBlur={commitHours} onKeyDown={e => { if (e.key === "Enter") commitHours(); }} />
            </Field>
          ) : <ReadRow label="Standard hours per program" tip="Standard hours per program" value={step?.standardHours == null ? "None" : String(step.standardHours)} hint={estimateLine || undefined} />}
          {editable ? (
            <Field label="Inventory pool" tip={<Tip term="Inventory pool" />}>
              <select className="select" value={step?.inventoryPool ?? ""} onChange={e => onStep(sel.id, { inventoryPool: e.target.value || null })}>
                <option value="">None</option>
                {Object.entries(INVENTORY_POOL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          ) : <ReadRow label="Inventory pool" tip="Inventory pool" value={step?.inventoryPool ? INVENTORY_POOL_LABELS[step.inventoryPool] ?? step.inventoryPool : "None"} />}
          {money ? (
            <div className="proc__derived">
              <span className="field__lt"><span>Labor at standard</span><Tip term="Labor at standard" /></span>
              {laborHidden ? <><b>Not shown</b><span className="small muted">It uses another company's rate.</span></> : <b className="tnum">{usd(laborAtStandard ?? 0)}</b>}
            </div>
          ) : <p className="proc__p muted small">Costs are hidden for your role.</p>}
        </>}
        <div className="proc__recorded">
          <span className="small muted">Recorded in</span>
          {module ? <span className="proc__module"><span>{module}</span><StatusChip tone="neutral" quiet>Planned</StatusChip></span>
            : <span className="small">No module yet. New activities are costed from the program plan until a module records them.</span>}
        </div>
      </div>
    </section>
  );
}

function ReadRow({ label, value, hint, tip }: { label: string; value: string; hint?: ReactNode; tip?: string }) {
  return (
    <div className="field">
      <span className="field__lt"><span className="field__label">{label}</span>{tip && <Tip term={tip} />}</span>
      <span className="proc__read">{value}</span>
      {hint && <small className="field__hint">{hint}</small>}
    </div>
  );
}

function SubmitDialog({ open, onClose, onSubmit }: { open: boolean; onClose(): void; onSubmit(note: string | null): Promise<void> }) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNote(""); setError(null); } }, [open]);
  return (
    <Dialog title="Submit the draft for approval" open={open} onClose={onClose} actions={<>
      <button type="button" className="btn" onClick={onClose}>Cancel</button>
      <button type="button" className="btn btn--primary" disabled={busy} onClick={async () => {
        setBusy(true);
        try { await onSubmit(note.trim() || null); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
      }}>Submit</button>
    </>}>
      <p>Another owner, controller or winemaker approves it. Changing the draft afterwards sends it back to draft.</p>
      <Field label="What changed (optional)" error={error}>
        <textarea className="input proc__note" maxLength={200} value={note} onChange={e => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function ApproveDialog({ open, version, note, onClose, onApprove }: { open: boolean; version: number; note: string | null; onClose(): void; onApprove(requestId: string): Promise<void> }) {
  const requestId = useMemo(() => (open ? crypto.randomUUID() : ""), [open]);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title={`Approve version ${version}`} open={open} onClose={onClose} actions={<>
      <button type="button" className="btn" onClick={onClose}>Cancel</button>
      <button type="button" className="btn btn--primary" data-autofocus disabled={busy} onClick={async () => { setBusy(true); try { await onApprove(requestId); } finally { setBusy(false); } }}>Approve</button>
    </>}>
      <p>Programs keep the version they were planned with. New plans and re-plans use version {version}.</p>
      {note && <blockquote className="proc__quote">{note}</blockquote>}
    </Dialog>
  );
}

function SendBackDialog({ open, onClose, onSend }: { open: boolean; onClose(): void; onSend(note: string): Promise<void> }) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setNote(""); setError(null); } }, [open]);
  return (
    <Dialog title="Send the draft back" open={open} onClose={onClose} actions={<>
      <button type="button" className="btn" onClick={onClose}>Cancel</button>
      <button type="button" className="btn btn--primary" onClick={async () => {
        if (!note.trim()) { setError("Say what needs to change."); return; }
        try { await onSend(note.trim()); } catch (e) { setError((e as Error).message); }
      }}>Send back</button>
    </>}>
      <Field label="What needs to change?" error={error}>
        <textarea className="input proc__note" maxLength={200} value={note} onChange={e => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function VersionsDialog({ open, onClose, onOpen }: { open: boolean; onClose(): void; onOpen(v: number): void }) {
  const q = useQuery({ queryKey: ["process", "versions"], queryFn: () => api<VersionRow[]>("/process/versions"), enabled: open });
  return (
    <Dialog title="Versions" open={open} onClose={onClose} side>
      {q.isPending ? <p className="muted">Loading versions…</p> : (
        <ol className="proc__versions">
          {(q.data ?? []).map(v => {
            const parts = [`Version ${v.version}`, v.publishedAt ? dateLabel(v.publishedAt) : null,
              v.isSample ? "Sample" : v.publishedBy ? `Approved by ${v.publishedBy}` : null,
              v.submittedBy ? `Submitted by ${v.submittedBy}` : null, v.preparedBy.length ? `Changed by ${people(v.preparedBy)}` : null].filter(Boolean);
            return (
              <li key={v.version}>
                <p className="proc__p"><b>{parts.join(" · ")}</b></p>
                {v.note && <p className="proc__p small">{v.note}</p>}
                <p className="proc__p small muted">{v.programs} {v.programs === 1 ? "program uses" : "programs use"} it{v.isCurrent ? " · Current" : ""}</p>
                <button type="button" className="btn btn--sm" aria-label={`Open version ${v.version}`} onClick={() => onOpen(v.version)}>Open</button>
              </li>
            );
          })}
        </ol>
      )}
    </Dialog>
  );
}
